import { useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingModal({ initial, mode, onClose, onSaved, onDeleted, onMarkAttendance }) {
  const { t } = useLang();
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };

  const [data, setData] = useState({
    date: initial?.date || "",
    time: initial?.time || "18:00",
    title: initial?.title || "",
    location: initial?.location || "",
    description: initial?.description || "",
    duration_minutes: initial?.duration_minutes || 60,
    price_cents: initial?.price_cents || 0,
    max_players: initial?.max_players || 0,
    training_type: initial?.training_type || "custom",
    notify_parents: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const inputCls =
    "w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#e8559a] focus:outline-none";

  const save = async (e) => {
    e.preventDefault();
    if (!data.title.trim() || !data.date) {
      setError(t("required_field"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (mode === "edit" && initial?.id) {
        const payload = { ...data };
        delete payload.notify_parents;
        const r = await axios.put(`${API}/admin/trainings/${initial.id}`, payload, { headers });
        onSaved(r.data);
      } else {
        const r = await axios.post(`${API}/admin/trainings`, data, { headers });
        onSaved(r.data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!initial?.id) return;
    if (!window.confirm("¿Eliminar este entrenamiento?")) return;
    try {
      await axios.delete(`${API}/admin/trainings/${initial.id}`, { headers });
      onDeleted(initial.id);
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      data-testid="training-modal"
      onClick={onClose}
    >
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#161616] border border-[#252525] rounded-2xl p-6 sm:p-7 my-8"
      >
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-[#252525]">
          <h2 className="font-bebas text-2xl sm:text-3xl tracking-widest text-white">
            ⚽ {mode === "edit" ? t("edit_training") : t("new_training")}
          </h2>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-white text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_title")} <span className="text-[#e8559a]">*</span>
            </label>
            <input
              data-testid="training-title-input"
              className={inputCls}
              value={data.title}
              placeholder="Pase y control · Tactical drills..."
              onChange={(e) => set("title", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_date")} <span className="text-[#e8559a]">*</span>
            </label>
            <input
              type="date"
              data-testid="training-date-input"
              className={inputCls}
              value={data.date}
              onChange={(e) => set("date", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_time")}
            </label>
            <input
              type="time"
              className={inputCls}
              value={data.time}
              onChange={(e) => set("time", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_location")}
            </label>
            <input
              className={inputCls}
              value={data.location}
              placeholder="Sloan's Lake Park · Denver"
              onChange={(e) => set("location", e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_duration")}
            </label>
            <input
              type="number"
              min={15}
              max={240}
              className={inputCls}
              value={data.duration_minutes}
              onChange={(e) => set("duration_minutes", Number(e.target.value) || 60)}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_type")}
            </label>
            <select
              data-testid="training-type-select"
              className={inputCls}
              value={data.training_type}
              onChange={(e) => {
                const tp = e.target.value;
                let price = data.price_cents;
                if (tp === "private_1") price = 7000;
                else if (tp === "private_2") price = 5000;
                else if (tp === "group") price = 4500;
                else if (tp === "custom") price = 0;
                set("training_type", tp);
                set("price_cents", price);
              }}
            >
              <option value="custom">{t("tt_custom")}</option>
              <option value="private_1">{t("tt_private_1")}</option>
              <option value="private_2">{t("tt_private_2")}</option>
              <option value="group">{t("tt_group")}</option>
              <option value="camp">{t("tt_camp")}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_price")}
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              data-testid="training-price-input"
              className={inputCls}
              value={(data.price_cents || 0) / 100}
              onChange={(e) => set("price_cents", Math.round(parseFloat(e.target.value || 0) * 100))}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_max_players")}
            </label>
            <input
              type="number"
              min={0}
              max={100}
              data-testid="training-max-players-input"
              className={inputCls}
              value={data.max_players}
              onChange={(e) => set("max_players", Number(e.target.value) || 0)}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1.5">
              {t("training_description")}
            </label>
            <textarea
              rows={3}
              className={`${inputCls} resize-none`}
              value={data.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Objetivos de la sesión, ejercicios..."
            />
          </div>
        </div>

        {mode === "create" && (
          <label className="mt-5 flex items-start gap-2.5 p-3 rounded-lg bg-[#e8559a]/[0.06] border border-[#e8559a]/30 cursor-pointer">
            <input
              type="checkbox"
              data-testid="notify-parents-checkbox"
              checked={data.notify_parents}
              onChange={(e) => set("notify_parents", e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded accent-[#e8559a]"
            />
            <span className="text-sm text-neutral-200">
              📧 {t("notify_parents")}
              <span className="block text-xs text-neutral-500 mt-0.5">
                Manda email automático a todos los padres registrados con fecha, hora y lugar
              </span>
            </span>
          </label>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 justify-between">
          <div>
            {mode === "edit" && (
              <>
                <button
                  type="button"
                  onClick={() => onMarkAttendance(initial.id)}
                  className="px-4 py-2 rounded-lg bg-[#e8559a]/10 border border-[#e8559a]/30 text-[#e8559a] text-sm font-medium hover:bg-[#e8559a]/20 transition-colors mr-2"
                >
                  ✓ {t("mark_attendance")}
                </button>
                <button
                  type="button"
                  onClick={del}
                  className="px-4 py-2 rounded-lg bg-[#e05555]/10 border border-[#e05555]/30 text-[#e05555] text-sm font-medium hover:bg-[#e05555]/20 transition-colors"
                >
                  🗑 {t("delete")}
                </button>
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 rounded-lg border border-[#252525] hover:border-[#e8559a]/50 text-neutral-300 text-sm transition-colors"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="training-save-btn"
              className="px-6 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas text-lg tracking-widest transition-colors"
            >
              {saving ? "..." : t("save")}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
